import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector, useDispatch } from 'react-redux';
import { hasPermission } from '../../../helpers/Permission';
import { PermissionTypes } from '../../../constants';
const styles = require('../styles.pcss');

import FlagNameSection from './FlagNameSection';
import PlatformSection from './PlatformSection';
import ScopeSection from './ScopeSection';
import DeleteFlagSection from './DeleteFlagSection';
import LoaderSection from './LoaderSection';

import {
  postFeature,
  resetFeatureExceptList,
} from '../../../actions/ManageTB/Features/Post';
import { putFeature } from '../../../actions/ManageTB/Features/Put';
import { deleteFeature } from '../../../actions/ManageTB/Features/Delete';
import {
  get as getAgencies,
  emptyResults as emptyAgencies,
} from 'actions/Agencies/Get';
import { get as getUsers, emptyResults as emptyUsers } from 'actions/Users/Get';
import {
  get as getCompanies,
  emptyResults as emptyCompanies,
} from 'actions/Companies/Get';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';

// Helper functions to replace lodash
const get = (obj: any, path: string, def?: any) => {
  if (!obj || (Array.isArray(obj) && obj.length === 0)) return def;
  try {
    return (
      path
        .split(/[.[\]]+/)
        .filter(Boolean)
        .reduce((a, c) => (a && a[c] !== undefined ? a[c] : undefined), obj) ??
      def
    );
  } catch {
    return def;
  }
};
const uniq = (arr: any[]) => Array.from(new Set(arr));
const isEmpty = (obj: any) => {
  if (Array.isArray(obj)) return obj.length === 0;
  if (typeof obj === 'object') return Object.keys(obj).length === 0;
  return !obj;
};
const remove = (arr: any[], fn: (v: any) => boolean) => {
  let i = arr.length;
  while (i--) {
    if (fn(arr[i])) arr.splice(i, 1);
  }
  return arr;
};
const find = (arr: any[], fn: (v: any) => boolean) => arr.find(fn);

const FeatureFlagPopup = ({
  openPopup,
  closePopup,
  addNewFeatureFlag,
  flag,
}: any) => {
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const companies = useSelector((state: any) =>
    (state.companies.companies || [])
      .slice()
      .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '')),
  );
  const isLoadingCompanies = useSelector(
    (state: any) => state.companies.isLoading,
  );
  const isLoadingFeatures = useSelector(
    (state: any) => state.features.isLoading,
  );
  const isLoadingAgencies = useSelector(
    (state: any) => state.agencies.isLoading,
  );
  const isLoadingUsers = useSelector((state: any) => state.users.isLoading);
  const agencies = useSelector((state: any) => state.agencies.agencies);
  const users = useSelector((state: any) => state.users.users);
  const features = useSelector((state: any) => state.features);

  const [name, setName] = useState(get(flag, '0.name', ''));
  const [friendlyName, setFriendlyName] = useState(
    get(flag, '0.friendlyName', ''),
  );
  const [errorName, setErrorName] = useState('');
  const [errorFriendlyName, setErrorFriendlyName] = useState('');
  const [errorPlatform, setErrorPlatform] = useState('');
  const [errorOrganizaition, setErrorOrganizaition] = useState('');
  const [errorAgency, setErrorAgency] = useState('');
  const [errorUser, setErrorUser] = useState('');
  const [waitingResponse, setWaitingResponse] = useState(false);
  const [confidence, setConfidence] = useState(
    get(flag, '0.confidence', 'preview'),
  );
  const [radioButtonState, setRadioButtonState] = useState(
    get(flag, '0.scope.type', 'all'),
  );
  const [selectedOrgs, setSelectedOrgs] = useState<any[]>([]);
  const [selectedAgencies, setSelectedAgencies] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [checkedItems, setCheckedItems] = useState(
    flag ? flag.map((f: any) => f.platform) : [],
  );
  const [flagVersions, setFlagVersions] = useState({
    android: get(
      find(flag, (p: any) => p.platform === 'android'),
      'version',
      '',
    ),
    androidUltra: get(
      find(flag, (p: any) => p.platform === 'androidUltra'),
      'version',
      '',
    ),
    api: get(
      find(flag, (p: any) => p.platform === 'api'),
      'version',
      '',
    ),
    ios: get(
      find(flag, (p: any) => p.platform === 'ios'),
      'version',
      '',
    ),
    iosUltra: get(
      find(flag, (p: any) => p.platform === 'iosUltra'),
      'version',
      '',
    ),
    web: get(
      find(flag, (p: any) => p.platform === 'web'),
      'version',
      '',
    ),
  });
  const [deleteFlag, setDeleteFlag] = useState(false);
  const [agencyMultiSelectValue, setAgencyMultiSelectValue] = useState('');
  const [userMultiSelectValue, setUserMultiSelectValue] = useState('');

  useEffect(() => {
    setName(get(flag, '0.name', ''));
    setFriendlyName(get(flag, '0.friendlyName', ''));
    setConfidence(get(flag, '0.confidence', 'preview'));
    setCheckedItems(
      flag && Array.isArray(flag) ? flag.map((f: any) => f.platform) : [],
    );
    setFlagVersions({
      android: get(
        find(flag || [], (p: any) => p.platform === 'android'),
        'version',
        '',
      ),
      androidUltra: get(
        find(flag || [], (p: any) => p.platform === 'androidUltra'),
        'version',
        '',
      ),
      api: get(
        find(flag || [], (p: any) => p.platform === 'api'),
        'version',
        '',
      ),
      ios: get(
        find(flag || [], (p: any) => p.platform === 'ios'),
        'version',
        '',
      ),
      iosUltra: get(
        find(flag || [], (p: any) => p.platform === 'iosUltra'),
        'version',
        '',
      ),
      web: get(
        find(flag || [], (p: any) => p.platform === 'web'),
        'version',
        '',
      ),
    });
    setRadioButtonState(
      Array.isArray(flag) && flag.length > 0
        ? get(flag, '0.scope.type', 'all')
        : 'all',
    );
  }, [flag]);

  useEffect(() => {
    if (radioButtonState === 'organization' && (!companies || companies.length < 1)) {
      dispatch(getCompanies({ name: '' }));
    }
    if (radioButtonState === 'agency' && (!agencies || agencies.length < 1)) {
      dispatch(getAgencies({ name: '', friendlyName: '', type: 'or' }));
    }
    if (radioButtonState === 'user' && (!users || users.length < 1)) {
      dispatch(getUsers({ email: '', type: 'or' }));
    }
    // Initialize selectedOrgs from flag if scope type is organization
    const scopeType = get(flag, '0.scope.type', 'all');
    if (scopeType === 'organization') {
      // Get organizations from flag (assumes flag[0].scope.organizations is an array of org ids)
      const orgIds = get(flag, '0.scope.organizations', []);
      // Map orgIds to company objects from companies list, if available
      const orgObjects = Array.isArray(orgIds)
        ? companies
            .filter((c: any) => orgIds.includes(c.id))
            .map((c: any) => ({ value: c.id, label: c.name }))
        : [];

      setSelectedOrgs(orgObjects);
    } else if (scopeType === 'agency') {
      const agencyIds = get(flag, '0.scope.agencies', []);
      const agencyObjects = Array.isArray(agencyIds)
        ? (agencies || [])
            .filter((a: any) => agencyIds.includes(a.id))
            .map((a: any) => ({ value: a.id, label: a.name }))
        : [];
      setSelectedAgencies(agencyObjects);
    } else if (scopeType === 'user') {
      const userIds = get(flag, '0.scope.users', []);
      const userObjects = Array.isArray(userIds)
        ? (users || [])
            .filter((u: any) => userIds.includes(u.id))
            .map((u: any) => ({
              value: u.id,
              label: `${u.firstName} ${u.lastName} - ${u.email}`,
            }))
        : [];
      setSelectedUsers(userObjects);
    } else {
      setSelectedOrgs([]);
      setSelectedAgencies([]);
      setSelectedUsers([]);
    }
  }, [radioButtonState, companies.length, agencies, users, dispatch]);

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setErrorName('');
  };
  const handleChangeFriendlyName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFriendlyName(e.target.value);
    setErrorFriendlyName('');
  };
  const onDeleteName = () => {
    setName('');
    setErrorName('');
  };
  const onDeleteFriendlyName = () => {
    setFriendlyName('');
    setErrorFriendlyName('');
  };
  const onChangeConfidence = (e: any) => {
    setConfidence(e.target.value);
    setErrorPlatform('');
  };
  const onChangeRadioButton = (e: any, value: string) => {
    setRadioButtonState(value);
    setErrorOrganizaition('');
    setErrorAgency('');
    setErrorUser('');
    setSelectedOrgs([]);
    setSelectedAgencies([]);
    setSelectedUsers([]);
  };
  const selectedOrgChange = (value: any) => {
    const seen = new Set<string>();
    const unique = (Array.isArray(value) ? value : []).filter((item: any) => {
      const key = item.value ?? item.id ?? item;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    setSelectedOrgs(unique);
    setErrorOrganizaition('');
  };
  const selectedAgenciesChange = (value: any) => {
    const seen = new Set<string>();
    const unique = (Array.isArray(value) ? value : []).filter((item: any) => {
      const key = item.value ?? item.id ?? item;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    setSelectedAgencies(unique);
    setErrorAgency('');
  };
  const selectedUsersChange = (value: any) => {
    const seen = new Set<string>();
    const unique = (Array.isArray(value) ? value : []).filter((item: any) => {
      const key = item.value ?? item.id ?? item;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    setSelectedUsers(unique);
    setErrorUser('');
  };
  const getCompaniesHandler = (value: string) => {
    dispatch(emptyCompanies());
    dispatch(getCompanies({ name: value }));
  };
  const getAgenciesHandler = (value: string) => {
    dispatch(emptyAgencies());
    dispatch(getAgencies({ name: value, friendlyName: value, type: 'or' }));
    setAgencyMultiSelectValue(value);
  };
  const getUsersHandler = (value: string) => {
    dispatch(emptyUsers());
    dispatch(getUsers({ email: value, type: 'or' }));
    setUserMultiSelectValue(value);
  };
  const onChange = (val: string) => (e: any, checked: boolean) => {
    let values = [...checkedItems];
    if (checked) {
      values.push(val);
    } else {
      values = values.filter((v) => v !== val);
    }
    setCheckedItems(values);
  };
  const isChecked = (val: string) => checkedItems.indexOf(val) > -1;
  const onChangeFlagVersion = (type: string, e: any) => {
    setFlagVersions((prev: any) => ({ ...prev, [type]: e.target.value }));
  };
  const handleClearFlagVersion = (type: string) => {
    setFlagVersions((prev: any) => ({ ...prev, [type]: '' }));
  };

  const onClickDeleteFlag = () => setDeleteFlag(true);
  const onClickCancelDeleteFlag = () => setDeleteFlag(false);
  const onClickConfirmDeleteFlag = () => {
    const toDelete = flag.map((item: any) => item.id);
    dispatch(deleteFeature(toDelete));
    closePopup();
  };

  const handleSubmit = () => {
    let errorName = '';
    let errorPlatform = '';
    let errorConfidence = '';
    let errorOrganizaition = '';
    let errorAgency = '';
    let errorUser = '';
    let isValid = true;
    if (name.trim() === '') {
      errorName = 'Required';
      isValid = false;
    }
    if (checkedItems.length < 1) {
      errorPlatform = 'Required';
      isValid = false;
    }
    if (confidence.trim() === '') {
      errorConfidence = 'Required';
      isValid = false;
    }
    if (radioButtonState === 'organization' && selectedOrgs.length < 1) {
      errorOrganizaition = 'Required';
      isValid = false;
    }
    if (radioButtonState === 'agency' && selectedAgencies.length < 1) {
      errorAgency = 'Required';
      isValid = false;
    }
    if (radioButtonState === 'user' && selectedUsers.length < 1) {
      errorUser = 'Required';
      isValid = false;
    }
    setErrorName(errorName);
    setErrorPlatform(errorPlatform);
    setErrorOrganizaition(errorOrganizaition);
    setErrorAgency(errorAgency);
    setErrorUser(errorUser);
    if (isValid) {
      let orgIds: any[] = [];
      let platform: any[] = [];
      let agencyIds: any[] = [];
      let userIds: any[] = [];
      let newScope: any = null;
      if (radioButtonState === 'organization') {
        orgIds = selectedOrgs.map((orgs: any) => orgs.value);
        newScope = { type: 'organization', organizations: orgIds };
      }
      if (radioButtonState === 'agency') {
        agencyIds = selectedAgencies.map((agency: any) => agency.value);
        newScope = { type: 'agency', agencies: agencyIds };
      }
      if (radioButtonState === 'user') {
        userIds = selectedUsers.map((user: any) => user.value);
        newScope = { type: 'user', users: userIds };
      }
      type PlatformType =
        | 'api'
        | 'androidUltra'
        | 'android'
        | 'ios'
        | 'iosUltra'
        | 'web';
      const flagVersionsCopy: Record<PlatformType, any> = { ...flagVersions };
      if (addNewFeatureFlag) {
        const checkedlist = uniq(checkedItems);
        checkedlist.forEach((item) => {
          const platformType = item as PlatformType;
          platform.push({
            platform: platformType,
            version: flagVersionsCopy[platformType],
          });
        });
        dispatch(
          postFeature({
            name,
            friendlyName,
            confidence,
            scope: newScope,
            platform,
          }),
        );
      } else {
        const checkedlist = uniq(checkedItems);
        checkedlist.forEach((item) => {
          const platformType = item as PlatformType;
          platform.push({
            platform: platformType,
            version: flagVersionsCopy[platformType],
            id: get(
              find(flag, (p: any) => p.platform === platformType),
              'id',
              undefined,
            ),
          });
        });
        let toDelete: any[] = [];
        flag.forEach((item: any) => {
          if (checkedItems.indexOf(item.platform) === -1) {
            toDelete.push(item.id);
          }
        });
        const confidenceUpdated =
          get(flag, '0.confidence', 'preview') !== confidence;
        const scopeUpdated =
          get(flag, '0.scope.type', 'all') !== radioButtonState;
        dispatch(
          putFeature(
            {
              name,
              friendlyName,
              confidence,
              scope: newScope,
              platform,
              toDelete,
              nameUpdate: name !== get(flag, '0.name', ''),
            },
            get(flag, '0.name', ''),
            confidenceUpdated,
            scopeUpdated,
          ),
        );
      }
      setWaitingResponse(true);
      closePopup();
    }
  };

  const closePopupHandler = () => {
    setName('');
    setAgencyMultiSelectValue('');
    setUserMultiSelectValue('');
    setFriendlyName('');
    setErrorName('');
    setErrorFriendlyName('');
    setErrorPlatform('');
    setErrorOrganizaition('');
    setErrorAgency('');
    setErrorUser('');
    setSelectedAgencies([]);
    setSelectedUsers([]);
    setWaitingResponse(false);
    setConfidence('');
    setRadioButtonState('all');
    setCheckedItems([]);
    setFlagVersions({
      android: '',
      androidUltra: '',
      api: '',
      ios: '',
      iosUltra: '',
      web: '',
    });
    setDeleteFlag(false);
    closePopup();
    dispatch(emptyCompanies());
    dispatch(emptyAgencies());
    dispatch(emptyUsers());
    dispatch(resetFeatureExceptList({}));
  };

  const title = addNewFeatureFlag ? 'Add Feature Flag' : 'Update Feature Flag';

  return (
    <Dialog
      fullWidth={true}
      maxWidth="md"
      className={classNames(styles.dialogBox, styles.ccPopup)}
      open={openPopup}
      onClose={closePopupHandler}
    >
      <div className={classNames(styles.title, styles.featureFlagPopup)}>
        <div className={styles.titleStyle}>{title}</div>
        <IconButton
          color="inherit"
          onClick={closePopupHandler}
          className={classNames(styles.closeIcon)}
          aria-label="Close"
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </div>
      <LoaderSection visible={isLoadingFeatures} />
      <div className={classNames(styles.updatePopup, styles.editTraveler)}>
        <FlagNameSection
          name={name}
          friendlyName={friendlyName}
          errorName={errorName}
          errorFriendlyName={errorFriendlyName}
          onChangeName={handleChangeName}
          onChangeFriendlyName={handleChangeFriendlyName}
          onDeleteName={onDeleteName}
          onDeleteFriendlyName={onDeleteFriendlyName}
        />
        <PlatformSection
          flagVersions={flagVersions}
          checkedItems={checkedItems}
          errorPlatform={errorPlatform}
          errorFriendlyName={errorFriendlyName}
          isLoadingFeatures={isLoadingFeatures}
          onChange={onChange}
          onChangeFlagVersion={onChangeFlagVersion}
          handleClearFlagVersion={handleClearFlagVersion}
          isChecked={isChecked}
        />
        <ScopeSection
          radioButtonState={radioButtonState}
          errorOrganizaition={errorOrganizaition}
          errorAgency={errorAgency}
          errorUser={errorUser}
          errorConfidence={errorPlatform}
          confidence={confidence}
          onChangeRadioButton={onChangeRadioButton}
          onChangeConfidence={onChangeConfidence}
          selectedOrgs={selectedOrgs}
          selectedAgencies={selectedAgencies}
          selectedUsers={selectedUsers}
          companies={companies.filter(
            (c: any) => !selectedOrgs.some((s: any) => s.value === c.id || s.value === c.value),
          )}
          agencies={(agencies || []).filter(
            (a: any) => !selectedAgencies.some((s: any) => s.value === a.id || s.value === a.value),
          )}
          users={(users || []).filter(
            (u: any) => !selectedUsers.some((s: any) => s.value === u.id || s.value === u.value),
          )}
          isLoadingCompanies={isLoadingCompanies}
          isLoadingAgencies={isLoadingAgencies}
          isLoadingUsers={isLoadingUsers}
          selectedOrgChange={selectedOrgChange}
          selectedAgenciesChange={selectedAgenciesChange}
          selectedUsersChange={selectedUsersChange}
          getCompanies={getCompaniesHandler}
          getAgencies={getAgenciesHandler}
          getUsers={getUsersHandler}
          isLoadingFeatures={isLoadingFeatures}
        />
        <DeleteFlagSection
          showDelete={
            !addNewFeatureFlag &&
            hasPermission('features', PermissionTypes.delete)
          }
          deleteFlag={deleteFlag}
          name={name}
          onClickDeleteFlag={onClickDeleteFlag}
          onClickCancelDeleteFlag={onClickCancelDeleteFlag}
          onClickConfirmDeleteFlag={onClickConfirmDeleteFlag}
        />
        <div className={classNames(styles.actions, styles.actionsRight)}>
          <div className={styles.popupButtonsContainer}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={closePopupHandler}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={handleSubmit}
              id="submit-feature-button"
            >
              {addNewFeatureFlag ? 'Add' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default FeatureFlagPopup;
